import React, { useEffect, useRef, useState } from 'react';
import {
  FaHandshake, FaGithub, FaPuzzlePiece, FaLightbulb,
} from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import * as PIXI from 'pixi.js';
import logo from '../assets/images/SR-Dev-Logo-Crown.png';

import '../assets/style/homePortfolioImg.scss';

const TAU = Math.PI * 2;
const HALF_PI = Math.PI / 2;
// #56769f #6f175d  background-image: linear-gradient(to right, #aa076b, #61045f);

Math.SineInOut = (percent, amp) => amp * (Math.sin(percent * TAU - HALF_PI) + 1) * 0.5;

const HomeTwo = () => {
  const containerRef = useRef(null);
  // const blobRef = useRef(null);
  const [isActive, setIsActive] = useState(false);

  const appRef = useRef(null);
  const graphicsRef = useRef(null);
  const totalVertices = 6;
  const amplitude = 150;
  let width;
  let height;
  let halfHeight;
  let space;

  const handleAnchorClick = (e) => {
    e.preventDefault();

    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const resize = () => {
    width = window.innerWidth;
    height = window.innerHeight;
    halfHeight = (width >= 321 && width <= 768) ? height / 5 : height / 2;
    space = width / totalVertices;
    if (appRef.current) {
      appRef.current.renderer.resize(width, height);
    }
  };

  // Helper method to create linear gradient bg
  const createGradientTexture = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#000'); // Start color
    gradient.addColorStop(1, '#2F2FA2'); // End color

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1, height);

    return PIXI.Texture.from(canvas);
  };

  const init = () => {
    appRef.current = new PIXI.Application({
      antialias: true,
      resolution: window.devicePixelRatio,
      transparent: true,
    });

    resize();
    window.addEventListener('resize', resize);
    const container = document.getElementById('pixi-container');
    container.appendChild(appRef.current.view);

    // Create a linear gradient background
    const bgGraphics = new PIXI.Graphics();
    const gradient = createGradientTexture();
    const bgSprite = new PIXI.Sprite(gradient);
    bgSprite.width = width;
    bgSprite.height = height;
    bgGraphics.addChild(bgSprite);
    appRef.current.stage.addChild(bgGraphics);

    graphicsRef.current = new PIXI.Graphics();
    appRef.current.stage.addChild(graphicsRef.current);
  };

  const bezier = (points) => {
    const size = points.length;
    const last = size - 4;
    graphicsRef.current.moveTo(points[0], points[1]);
    for (let i = 0; i < size - 2; i += 2) {
      const x0 = i ? points[i - 2] : points[0];
      const y0 = i ? points[i - 1] : points[1];
      const x1 = points[i + 0];
      const y1 = points[i + 1];
      const x2 = points[i + 2];
      const y2 = points[i + 3];
      const x3 = i !== last ? points[i + 4] : x2;
      const y3 = i !== last ? points[i + 5] : y2;
      const cp1x = x1 + (x2 - x0) / 6;
      const cp1y = y1 + (y2 - y0) / 6;

      const cp2x = x2 - (x3 - x1) / 6;
      const cp2y = y2 - (y3 - y1) / 6;
      graphicsRef.current.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x2, y2);
    }
  };

  const render = () => {
    const time = new Date().getTime() * 0.001;
    graphicsRef.current.clear();
    // graphicsRef.current.beginFill(0xff1361);
    graphicsRef.current.beginFill(0xFFFFFF);
    const points = [];
    for (let i = 0; i <= totalVertices; i += 1) {
      const x = space * i;
      const amp = Math.sin(time - i) * amplitude;
      const y = Math.SineInOut(i / totalVertices, amp);
      points.push(x, halfHeight + y);
    }
    bezier(points);
    graphicsRef.current.lineTo(width, height);
    graphicsRef.current.lineTo(0, height);
    graphicsRef.current.lineTo(0, halfHeight);
    graphicsRef.current.endFill();
  };

  const animate = () => {
    requestAnimationFrame(animate);
    render();
  };

  useEffect(() => {
    // Initialize IntersectionObserver
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        // Start animation when section is in view
        setIsActive(true);
        animate();
      } else {
        // Stop animation when section goes out of view
        setIsActive(false);
        cancelAnimationFrame(containerRef.current);
      }
    }, { threshold: 0 });

    // Observe the section
    observer.observe(containerRef.current);

    // Initialize PIXI once when the component mounts
    init();

    // Cleanup IntersectionObserver, PIXI, and event listener when the component unmounts
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resize);
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null; // Ensure reference is cleared
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <img className="logo-img" src={logo} alt="logo" />
      <div ref={containerRef} id="pixi-container">
        <div className="hero">
          <div className="hero__wrapper">
            <div className="hero__first">
              <h1 className={`hero-intro-col-1--header${isActive ? ' flicker' : ''}`}>
                <span>Full-</span>
                <br />
                <span>Stack</span>
                <br />
                <span>Developer</span>
              </h1>
              <p className="hero-intro-col-1--para">
                Said is a Senior Full-Stack Developer, crafting innovative,
                user-friendly websites with precision and creativity,
                setting a benchmark in the market.
                His dedication to excellence and passion for technology
                drive him to deliver outstanding digital experiences.
              </p>
              <div className="hero-intro-col-1--buttons">
                <a className="btn1 btn-11" onClick={handleAnchorClick} href="/#contact-section">
                  HIRE ME
                  <MdEmail style={{ position: 'relative', width: '20px', minWidth: '20px' }} />
                </a>
                <a className="btn1 btn-11 extra-stylee" href="https://github.com/SaidRasinlic" target="_blank" rel="noreferrer">
                  VIEW GITHUB
                  <FaGithub style={{ position: 'relative', width: '20px', minWidth: '20px' }} />
                </a>
              </div>
            </div>
            <div className="hero__second">
              <div className="blob-wrapper">
                <div className={`blob-img${isActive ? ' animate' : ''}`} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="home-data">
        <div className="home-data__col">
          <h3 className="home-data__header">
            Inovative approach
            <FaLightbulb />
          </h3>
          <p>
            From designing elegant user interfaces to implementing efficient algorithms,
            I thrive on pushing boundaries and thinking outside
            the box to create exceptional software experiences.
          </p>
        </div>
        <div className="home-data__col">
          <h3 className="home-data__header">
            Adaptive skillset
            <FaPuzzlePiece />
          </h3>
          <p>
            Whether it&apos;s mastering a new framework or seamlessly integrating diverse
            components, my ability to adapt ensures that I deliver robust
            and scalable solutions tailored to every challenge.
          </p>
        </div>
        <div className="home-data__col">
          <h3 className="home-data__header">
            Collaborative spirit
            <FaHandshake />
          </h3>
          <p>
            With strong communication skills and a commitment to shared goals,
            I foster a collaborative environment where ideas flourish,
            and projects thrive, driving success in every endeavor.
          </p>
        </div>
      </div>
    </>
  );
};

export default HomeTwo;
